import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import bevanImage from './bevan.jpg';
import texasImage from './texas.jpg';
import papiImage from './papi.jpg';

const images = [
  { src: bevanImage, text: 'Bevan is the Goat', color: '#6a11cb' },
  { src: texasImage, text: 'Texas is a Chad', color: '#11998e' },
  { src: papiImage, text: 'Papi is a Bum', color: '#fc4a1a' }
];

const LoadingScreen = ({ onComplete }) => {
  const [currentImage, setCurrentImage] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const timer = setInterval(() => {
      if (currentImage < images.length - 1) {
        setCurrentImage(currentImage + 1);
      } else {
        clearInterval(timer);
        setIsVisible(false);
        setTimeout(onComplete, 1000);
      }
    }, 3000);
    return () => clearInterval(timer);
  }, [currentImage, onComplete]);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1 }}
        >
          <Box sx={{
            height: '100vh',
            width: '100vw',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'fixed',
            top: 0,
            left: 0,
            zIndex: 9999,
            overflow: 'hidden',
            background: `linear-gradient(135deg, ${images[currentImage].color} 0%, #000000 100%)`,
            transition: 'background 1s ease-in-out'
          }}>
            {/* Animated background elements */}
            <motion.div
              style={{
                position: 'absolute',
                top: '-50%',
                left: '-50%',
                width: '200%',
                height: '200%',
                background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
              }}
              animate={{
                rotate: 360,
              }}
              transition={{
                duration: 30,
                repeat: Infinity,
                ease: 'linear'
              }}
            />
            
            {/* Floating particles */}
            {[...Array(20)].map((_, i) => (
              <motion.div
                key={i}
                style={{
                  position: 'absolute',
                  background: 'rgba(255,255,255,0.6)',
                  borderRadius: '50%',
                  width: `${Math.random() * 6 + 2}px`,
                  height: `${Math.random() * 6 + 2}px`,
                }}
                initial={{
                  x: Math.random() * 100,
                  y: Math.random() * 100,
                  opacity: 0
                }}
                animate={{
                  x: Math.random() * 100,
                  y: Math.random() * 100,
                  opacity: [0, 0.6, 0],
                  transition: {
                    duration: Math.random() * 10 + 10,
                    repeat: Infinity,
                    repeatType: 'reverse'
                  }
                }}
              />
            ))}

            <Box sx={{
              position: 'relative',
              zIndex: 2,
              textAlign: 'center',
              padding: 4,
              borderRadius: '24px',
              backdropFilter: 'blur(10px)',
              backgroundColor: 'rgba(0,0,0,0.3)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <AnimatePresence mode='wait'>
                <motion.div
                  key={currentImage}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 1.2 }}
                  transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                >
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <motion.img
                      src={images[currentImage].src}
                      alt={images[currentImage].text}
                      style={{
                        width: '250px',
                        height: '250px',
                        objectFit: 'cover',
                        borderRadius: '16px',
                        boxShadow: `0 10px 30px ${images[currentImage].color}80`,
                        border: `2px solid ${images[currentImage].color}`
                      }}
                      initial={{ rotate: -5 }}
                      animate={{
                        rotate: [0, 5, 0],
                        transition: {
                          duration: 4,
                          repeat: Infinity,
                          repeatType: 'reverse',
                          ease: 'easeInOut'
                        }
                      }}
                    />
                  </motion.div>
                </motion.div>
              </AnimatePresence>

              <AnimatePresence mode='wait'>
                <motion.div
                  key={`text-${currentImage}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  <Typography
                    variant="h3"
                    sx={{
                      color: '#ffffff',
                      fontWeight: 'bold',
                      fontFamily: '"Playfair Display", serif',
                      mt: 4,
                      textShadow: `0 0 15px ${images[currentImage].color}`,
                      letterSpacing: '1px',
                      background: `linear-gradient(90deg, #ffffff, ${images[currentImage].color})`,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundSize: '200% auto',
                      animation: 'gradient 3s ease infinite'
                    }}
                  >
                    {images[currentImage].text}
                  </Typography>
                </motion.div>
              </AnimatePresence>

              <Box sx={{
                width: '100%',
                height: '4px',
                background: 'rgba(255,255,255,0.2)',
                borderRadius: '2px',
                mt: 4,
                overflow: 'hidden'
              }}>
                <motion.div
                  initial={{ width: 0 }}
                  animate={{
                    width: '100%',
                    transition: {
                      duration: 3 * images.length,
                      ease: 'linear'
                    }
                  }}
                  style={{
                    height: '100%',
                    background: `linear-gradient(90deg, ${images[currentImage].color}, #ffffff)`,
                    borderRadius: '2px'
                  }}
                />
              </Box>
            </Box>

            {/* Signature */}
            <Typography
              variant="caption"
              sx={{
                position: 'absolute',
                bottom: 20,
                right: 20,
                color: 'rgba(255,255,255,0.5)',
                fontFamily: '"Dancing Script", cursive',
                fontSize: '1.2rem'
              }}
            >
             YIELD OPTIMIZER
            </Typography>
          </Box>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LoadingScreen;